import { asAccountId } from "@repo/app-types/entities/account";
import {
  buildTeammateDisplayNames,
  TeammateType,
} from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import {
  CustomerTimeline,
  type CustomerTimelineActivity,
} from "@repo/ui/customerTimeline/customerTimeline";
import { CustomerTimelineCall } from "@repo/ui/customerTimeline/customerTimelineCall";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { IconPhone } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useMemo, type FC } from "react";
import { useParams } from "react-router";
import {
  useCustomerDetailQuery,
  useCustomerTimelineInfiniteQuery,
} from "../../api/entities/customer.js";
import { requireRouteParam } from "../../app/routeParams.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import { PH_MASK_CLASS } from "../analytics/sessionRecordingMasking.js";
import { renderCallLength } from "../teammatePerformance/durationFormatting.js";

export const CustomerDetailPage: FC = () => {
  const routeParams = useParams();
  const accountId = asAccountId(requireRouteParam(routeParams, "accountId"));
  const detailResult = useCustomerDetailQuery(accountId);
  const timelineResult = useCustomerTimelineInfiniteQuery(accountId);
  const detail = detailResult.currentData;
  const pages = timelineResult.currentData?.pages;

  const activities = useMemo<readonly CustomerTimelineActivity[]>(
    () =>
      (pages ?? []).flatMap((page) =>
        page.calls.map((call) => ({
          id: call.id,
          dateTime: call.recorded_at,
          dateLabel: DateTime.fromISO(call.recorded_at).toFormat(
            "MMM d, h:mm a",
          ),
          icon: <IconPhone className="size-4" />,
          content: (
            <CustomerTimelineCall
              title={call.title || "Call recording"}
              participants={call.teammates.map((teammate) => ({
                id: teammate.id,
                name: buildTeammateDisplayNames(teammate).primaryDisplayName,
                kind:
                  teammate.type === TeammateType.AGENT ? "voiceAgent" : "human",
              }))}
              playbookNames={call.playbook_names}
              duration={
                call.duration_seconds == null
                  ? undefined
                  : renderCallLength(call.duration_seconds)
              }
            />
          ),
        })),
      ),
    [pages],
  );

  const isError =
    (detailResult.isError && !detail) || (timelineResult.isError && !pages);

  const renderTimeline = () => {
    if (isError) {
      return (
        <p className="text-sm text-muted-foreground">
          Unable to load customer.
        </p>
      );
    }

    if (!pages) {
      return <p className="text-sm text-muted-foreground">Loading timeline…</p>;
    }

    return (
      <div className={PH_MASK_CLASS}>
        <CustomerTimeline activities={activities} />
        {timelineResult.hasNextPage && (
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.SMALL}
            className="mt-5"
            onClick={() => void timelineResult.fetchNextPage()}
            isDisabled={timelineResult.isFetchingNextPage}
            isLoading={timelineResult.isFetchingNextPage}
          >
            Load more activity
          </Button>
        )}
      </div>
    );
  };

  return (
    <PageLayout>
      <PageHeader
        title={
          <span className={PH_MASK_CLASS}>{detail?.name ?? "Customer"}</span>
        }
        isLoading={detailResult.isLoading}
      />
      <PageBody>{renderTimeline()}</PageBody>
    </PageLayout>
  );
};
